import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { chatSliceName } from "./constants";
import { ChatMessage } from "./types/chat-message";
import { getChatMessages } from "./thunks/get-chat-messages";
import { NewResponse } from "./types/new-response";
import { IntentType } from "./contants/intent-type";
import { handleNewResponseComplete } from "./thunks/handleNewResponseComplete";
import { sendFeedbackEmoj } from "./thunks/send-feedback-emoj";
import { sendFeedback } from "./thunks/send-feedback";
import { InputCollection } from "./types/input-collection";
import { sendInputSubmission } from "./thunks/send-input-submission";
import { SuggestionChip } from "./types/suggestion-chip";
import { postEndLiveAgentChat } from "./thunks/post-end-live-agent-chat";
import { shouldDisplay } from "../app-state/thunks/should-display";
import { AgentDetails } from "../theme/types/agent-details";
import { MeetingDetails } from "../theme/types/meeting-details";
import { AvailableSlots } from "../theme/types/available-slots";

import { RequestNudgeType } from "../../../application/utils/request-nudge-type";
import { RequestNudgeConfig } from "../../types/dto/chat/request-nudge-config";

export interface Chat {
  messages: Array<ChatMessage>;
  isMessagesLoading: boolean;
  isStreamingHandling: boolean;
  isDoneAnimationBuffer: boolean;
  buffer: string;
  streamingMessageTurnId?: string;
  suggestionChips: SuggestionChip[];
  inputCollection: InputCollection | null;
  getFeedback: boolean;
  isWaitingLiveAgent: boolean;
  isChattingWithLiveAgent: boolean;
  isPingedByLA: boolean;
  quickActionButtons: {
    displayLiveAgentConnect: boolean,
    displayCalendar: boolean,
  },
  customCalendar?: boolean,
  agentDetails? : AgentDetails
  meetingDetails? : MeetingDetails
  availaleSlots? : AvailableSlots
  requestNudgeConfig?: RequestNudgeConfig;
  reqNudgeType?: RequestNudgeType;
}

const initialState: Chat = {
  messages: [],
  isMessagesLoading: false,
  isStreamingHandling: false,
  isDoneAnimationBuffer: true,
  buffer: '',
  suggestionChips: [],
  inputCollection: null,
  getFeedback: false,
  isWaitingLiveAgent: false,
  isChattingWithLiveAgent: false,
  isPingedByLA: false,
  quickActionButtons: {
    displayLiveAgentConnect: false,
    displayCalendar: false,
  },
  customCalendar: false,
  agentDetails: undefined,
  meetingDetails: undefined,
  availaleSlots: undefined,
  reqNudgeType: RequestNudgeType.CONVERSATION_START,
}

export const chatSlice = createSlice({
  initialState,
  name: chatSliceName,
  reducers: {
    resetChatState: (state) => {
      state.messages = [];
      state.isMessagesLoading = false;
      state.isStreamingHandling = false;
      state.isDoneAnimationBuffer = true;
      state.buffer = '';
      state.suggestionChips = [];
      state.inputCollection = null;
      state.getFeedback = false;
      state.isChattingWithLiveAgent = false;
      state.isPingedByLA = false;
    },
    setIsMessagesLoading: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isMessagesLoading = payload;
    },
    setCustomCalendar: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;
      state.customCalendar = payload;
    },
    setAgentDetails: (state, action: PayloadAction<AgentDetails>) => {
      const { payload } = action;
      state.agentDetails = payload;
    },
    setMeetingDetails: (state, action: PayloadAction<MeetingDetails>) => {
      const { payload } = action;
      state.meetingDetails = payload;
    },
    setAvailableSlots: (state, action: PayloadAction<AvailableSlots>) => {
      const { payload } = action;
      state.availaleSlots = payload;
    },
    setMessages: (state, action: PayloadAction<Array<ChatMessage>>) => {
      const { payload } = action;
      
      state.messages = payload;
    },
    setInputCollection: (state, action: PayloadAction<InputCollection | null>) => {
      const { payload } = action;

      state.inputCollection = payload;
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages = [...state.messages, action.payload];
    },
    setIsStreamingHandling: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isStreamingHandling = payload;
    },
    setStreamingMessageTurnId: (state, action: PayloadAction<string>) => {
      const { payload } = action;

      state.streamingMessageTurnId = payload;
    },
    setIsChattingWithLiveAgent: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isChattingWithLiveAgent = payload;
    },
    setIsPingedByLA: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isPingedByLA = payload;
    },
    setIsWaitingLiveAgent: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.isWaitingLiveAgent = payload;
    },
    setReqNudgeConfig: (state, action: PayloadAction<RequestNudgeConfig>) => {
      const { payload } = action;
      state.requestNudgeConfig = payload;
    },
    setReqNudgeType: (state, action: PayloadAction<RequestNudgeType>) => {
      const { payload } = action;
      state.reqNudgeType = payload;
    },
    setGetFeedback: (state, action: PayloadAction<boolean>) => {
      const { payload } = action;

      state.getFeedback = payload;
    },
    handleNewResponse: (state, action: PayloadAction<{response: NewResponse, lastAgentTurnId: string}>) => {
      const { payload: { response, lastAgentTurnId } } = action;
      if (lastAgentTurnId !== response.turn_id) return;

      switch(response.intent) {
        case IntentType.SUGGESTION_CHIPS:
          const { suggestion_chips } = response;
          
          if (suggestion_chips) {
            state.suggestionChips = suggestion_chips.map((chip) => ({
              text: typeof chip === 'string' ? chip as string : chip.text,
              type: typeof chip === 'string' ? 'text' : chip.type,
            }));
          }
        break;
        case IntentType.QNA:
          if (!response.system_text_response) {
            break;
          }
          state.buffer += response.system_text_response;
        break;
        case IntentType.INPUT_COLLECTION:
          state.suggestionChips = [];
          state.inputCollection = {
            inputLabel: response.input_label || '',
            placeholder: response.shadow_text || '',
          };
        break;
      }
    },
    setSuggestionChips: (state, action: PayloadAction<SuggestionChip[]>) => {
      const { payload } = action;

      state.suggestionChips = payload;
    },
    setIsDoneAnimationBuffer: (state, action: PayloadAction<boolean>) => {
      state.isDoneAnimationBuffer = action.payload;
    },
    stopWaitingLA: (state) => {
      state.streamingMessageTurnId = '';
      state.isDoneAnimationBuffer = true;
      state.buffer = '';
      state.isStreamingHandling = false;
      state.isWaitingLiveAgent = false;
    },
    onDoneAnimationBuffer: (state, action: PayloadAction<{conversationId: string}>) => {
      state.isDoneAnimationBuffer = true;

      if (!state.isStreamingHandling) {
        state.messages.push({
          itemType: "A",
          value: state.buffer,
          turn_id: state.streamingMessageTurnId!,
          conversation_id: action.payload.conversationId
        });
        state.buffer = '';
        delete state.streamingMessageTurnId;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getChatMessages.rejected, (state) => {
      state.messages = [];
    });

    builder.addCase(getChatMessages.fulfilled, (state, action) => {
      const { payload: { messages } } = action;
      state.messages = messages;

      const lastMessage = messages[messages.length - 1];
      if (!lastMessage) return;

      state.suggestionChips = lastMessage.suggestion_chips || [];
      state.inputCollection = lastMessage.input_label
        ? {
          inputLabel: lastMessage.input_label || '',
          placeholder: lastMessage.shadow_text || '',
        }
        : state.inputCollection;
    });

    builder.addCase(handleNewResponseComplete.fulfilled,(_, action) => {
      const { payload } = action;

      return payload;
    });

    builder.addCase(handleNewResponseComplete.rejected,(_, action) => {

      const { payload } = action;

      return payload as Chat;
    });

    builder.addCase(sendFeedbackEmoj.fulfilled, (_, action) => {

      const { payload } = action;

      return payload;
    });

    builder.addCase(sendFeedbackEmoj.rejected, () => {
    });

    builder.addCase(sendFeedback.fulfilled, (_, action) => {

      const { payload } = action;

      return payload;
    });

    builder.addCase(sendFeedback.rejected, () => {
    });

    builder.addCase(sendInputSubmission.fulfilled, (state) => {
      state.inputCollection = null;
    });

    builder.addCase(sendInputSubmission.rejected, () => {
    });

    builder.addCase(postEndLiveAgentChat.fulfilled, (state) => {
      state.isChattingWithLiveAgent = false;
    });

    builder.addCase(shouldDisplay.fulfilled, (state, action) => {
      const { payload } = action;

      state.quickActionButtons = {
        displayCalendar: !!payload?.display_calendar_quick_action_button,
        displayLiveAgentConnect: !!payload?.display_live_agent_connect_quick_action_button,
      }
    });
  }
});

export const {
  setIsMessagesLoading,
  setMessages,
  addMessage,
  setIsStreamingHandling,
  handleNewResponse,
  setSuggestionChips,
  setInputCollection,
  onDoneAnimationBuffer,
  setIsDoneAnimationBuffer,
  setGetFeedback,
  setIsChattingWithLiveAgent,
  setStreamingMessageTurnId,
  resetChatState,
  setIsWaitingLiveAgent,
  stopWaitingLA,
  setIsPingedByLA,
  setCustomCalendar,
  setAgentDetails,
  setMeetingDetails,
  setAvailableSlots,
  setReqNudgeConfig,
  setReqNudgeType
} = chatSlice.actions;
